import React, { useState, useEffect, useRef } from 'react';
import { Search, MapPin, X } from 'lucide-react';
import { apiRequest } from '@/config/api';

interface LocationOption {
  location_text: string;
  city: string;
  state: string;
  country: string;
  country_code: string;
}

interface LocationAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  error?: string;
}

export default function LocationAutocomplete({
  value,
  onChange,
  placeholder = "Search for a city...",
  disabled = false,
  required = false,
  className = "",
  error
}: LocationAutocompleteProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState(value);
  const [options, setOptions] = useState<LocationOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [popularLocations, setPopularLocations] = useState<LocationOption[]>([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  // Fetch popular locations on mount
  useEffect(() => {
    fetchPopularLocations();
  }, []);

  // Update searchTerm when value prop changes
  useEffect(() => {
    setSearchTerm(value);
  }, [value]);

  const fetchPopularLocations = async () => {
    try {
      const response = await apiRequest('locations/popular');
      if (response.ok) {
        const data = await response.json();
        setPopularLocations(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching popular locations:', error);
    }
  };

  const searchLocations = async (query: string) => {
    if (query.length < 2) {
      setOptions(popularLocations);
      return;
    }

    setLoading(true);
    try {
      const response = await apiRequest(`locations/search?q=${encodeURIComponent(query)}&limit=10`);
      if (response.ok) {
        const data = await response.json();
        setOptions(data.data || []);
      } else {
        setOptions([]);
      }
    } catch (error) {
      console.error('Error searching locations:', error);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    setHighlightedIndex(-1);
    
    // Clear previous debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    
    // Debounce search
    debounceRef.current = setTimeout(() => {
      searchLocations(newValue);
    }, 300);
  };

  const handleInputFocus = () => {
    setIsOpen(true);
    if (options.length === 0) {
      setOptions(popularLocations);
    }
  };

  const handleOptionSelect = (option: LocationOption) => {
    setSearchTerm(option.location_text);
    onChange(option.location_text);
    setIsOpen(false);
    setHighlightedIndex(-1);
  };

  const handleClear = () => {
    setSearchTerm('');
    onChange('');
    setOptions(popularLocations);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < options.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : prev);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && options[highlightedIndex]) {
          handleOptionSelect(options[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (listRef.current && !listRef.current.contains(event.target as Node) &&
          inputRef.current && !inputRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const displayOptions = options.length > 0 ? options : popularLocations;

  return (
    <div className={`relative ${className}`}>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search className="h-4 w-4 text-gray-400" />
        </div>
        
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          className={`
            w-full pl-10 pr-10 py-2 border rounded-md shadow-sm
            focus:ring-2 focus:ring-blue-500 focus:border-blue-500
            disabled:bg-gray-50 disabled:cursor-not-allowed
            ${error ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'}
          `}
        />
        
        {searchTerm && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute inset-y-0 right-0 pr-3 flex items-center"
          >
            <X className="h-4 w-4 text-gray-400 hover:text-gray-600" />
          </button>
        )}
      </div>

      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}

      {isOpen && (
        <div
          ref={listRef}
          className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto"
        >
          {loading && (
            <div className="px-4 py-2 text-sm text-gray-500 flex items-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-2"></div>
              Searching...
            </div>
          )}
          
          {!loading && displayOptions.length === 0 && searchTerm.length >= 2 && (
            <div className="px-4 py-2 text-sm text-gray-500">
              No locations found for "{searchTerm}"
            </div>
          )}
          
          {!loading && displayOptions.length === 0 && searchTerm.length < 2 && (
            <div className="px-4 py-2 text-sm text-gray-500">
              Type at least 2 characters to search
            </div>
          )}
          
          {!loading && displayOptions.length > 0 && (
            <>
              {searchTerm.length < 2 && (
                <div className="px-4 py-2 text-xs text-gray-400 border-b border-gray-100">
                  Popular Locations
                </div>
              )}
              
              {displayOptions.map((option, index) => (
                <div
                  key={`${option.city}-${option.state}-${option.country}`}
                  onClick={() => handleOptionSelect(option)}
                  className={`
                    px-4 py-2 cursor-pointer flex items-center
                    ${index === highlightedIndex ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-50'}
                  `}
                >
                  <MapPin className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
                  <span className="text-sm">{option.location_text}</span>
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}
